import { Component, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs';

import { NotificationContainerComponent } from '@core/components/notification-container/notification-container.component';
import { OverlayHostComponent } from '@core/components/overlay-host/overlay-host.component';
import { WindowType } from '@core/models/sync-data.model';
import { NotificationService } from '@core/services/notification/notification.service';
import { selectIsPermissionEnabled } from '@core/store/auth/auth.selectors';
import { markAsPopup } from '@core/store/data-visualization-popup/data-visualization-popup.actions';
import { PopupDataVisualizationReplicator } from '@core/store/data-visualization-popup/data-visualization-popup.replicator';
import { timelineModificationOutcome } from '@core/store/data-visualization/data-visualization.actions';

import { DataVisuComponent } from '@shared/components/data-visu/data-visu.component';

import { EntityAutoCompleteHostComponent } from '@features/components/entities/entity-auto-complete-dialog/entity-auto-complete-host.component';

/**
 * This class will work as a wrapper for the data visualization component. It will listen to
 * messages comming from the main window and replicate them to the popup.
 *
 * The popup is a top-level route outside both the app shell and the authenticated layout, so
 * it mounts the window-scoped singletons itself. Without them this window has no dropdown, no
 * context menu, no notifications and no entity-select dialog — each fails silently, because
 * every one of those services no-ops until a component registers with it.
 */
@Component({
  selector: 'app-data-visualization-popup',
  standalone: true,
  templateUrl: './data-visualization-popup.component.html',
  imports: [
    DataVisuComponent,
    OverlayHostComponent,
    NotificationContainerComponent,
    EntityAutoCompleteHostComponent
  ]
})
export class DataVisualizationPopupComponent implements OnInit {
  private readonly replicator = inject(PopupDataVisualizationReplicator);
  private readonly route = inject(ActivatedRoute);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly notificationService = inject(NotificationService);

  protected isVisuAllowed = this.store.selectSignal(selectIsPermissionEnabled('visu'));

  constructor() {
    // Writes asked for from this window are performed by the window holding the live
    // modification actions, so its notifications are the only report this one ever gets.
    this.actions$
      .pipe(
        ofType(timelineModificationOutcome),
        filter(({ requesterId }) => requesterId === this.replicator.windowId.id),
        takeUntilDestroyed()
      )
      .subscribe(({ level, title, message }) => {
        this.notificationService[level](title, message);
      });
  }

  ngOnInit(): void {
    this.store.dispatch(markAsPopup());
    const id = this.route.snapshot.queryParamMap.get(
      PopupDataVisualizationReplicator.WINDOW_ID_PARAM
    ) as string;
    const type = this.route.snapshot.queryParamMap.get(
      PopupDataVisualizationReplicator.WINDOW_TYPE_PARAM
    ) as WindowType;
    const popupId = this.route.snapshot.queryParamMap.get(
      PopupDataVisualizationReplicator.POPUP_ID_PARAM
    ) as string;
    this.replicator.register(
      { id: popupId, type: 'data-visualization-popup' },
      { id, type }
    );
  }
}
